const ON_CHANGE_DEBOUNCE_TIMER = 300;

const PUB_SUB_EVENTS = {
  cartUpdate: 'cart-update',
  quantityUpdate: 'quantity-update',
  optionValueSelectionChange: 'option-value-selection-change',
  variantChange: 'variant-change',
  cartError: 'cart-error',
};

let subscribers = {};

function subscribe(eventName, callback) {
  if (subscribers[eventName] === undefined) {
    subscribers[eventName] = [];
  }

  subscribers[eventName] = [...subscribers[eventName], callback];

  return function unsubscribe() {
    subscribers[eventName] = subscribers[eventName].filter((cb) => {
      return cb !== callback;
    });
  };
}

function publish(eventName, data) {
  if (subscribers[eventName]) {
    subscribers[eventName].forEach((callback) => {
      callback(data);
    });
  }
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  static #supportsViewTransitions = typeof document.startViewTransition === 'function';



  /**
   * Updates an HTML node with new content using View Transitions API when available.
   * Falls back to direct replacement when View Transitions API is not supported.
   */
  static async viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    // Store current scroll position
    const scrollPos = {
      x: window.scrollX,
      y: window.scrollY
    };

    // Run pre-process callbacks
    preProcessCallbacks?.forEach(callback => callback(newContent));

    // Prepare the new node
    const newNode = HTMLUpdateUtility.#prepareNewNode(newContent);
    
    try {
      if (HTMLUpdateUtility.#supportsViewTransitions) {
        await document.startViewTransition(() => {
          HTMLUpdateUtility.#performDOMUpdate(oldNode, newNode);
          // Run post-process callbacks
          postProcessCallbacks?.forEach(callback => callback(newNode));
        }).finished;
      } else {
        HTMLUpdateUtility.#performDOMUpdate(oldNode, newNode);
        // Run post-process callbacks
        postProcessCallbacks?.forEach(callback => callback(newNode));
      }

      // Restore scroll position
      window.scrollTo(scrollPos.x, scrollPos.y);
    } catch (error) {
      console.error('View transition failed:', error);
      // Fallback to direct replacement
      HTMLUpdateUtility.#performDOMUpdate(oldNode, newNode);
      postProcessCallbacks?.forEach(callback => callback(newNode));
    }
  }

  /**
   * Prepares a new node from content, handling script execution
   */
  static #prepareNewNode(content) {
    const wrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(wrapper, content.outerHTML);
    return wrapper.firstChild;
  }

  /**
   * Performs the actual DOM update
   */
  static #performDOMUpdate(oldNode, newNode) {
    // Handle ID conflicts
    HTMLUpdateUtility.#dedupeIds(oldNode);
    
    // Replace old node with new node
    oldNode.replaceWith(newNode);
  }

  /**
   * Handles persisted pages (from bfcache)
   */
  static #handlePersistedPage() {
    // Re-run any necessary initialization
    document.dispatchEvent(new CustomEvent('DOMContentLoaded'));
  }

  /**
   * Dedupe IDs to prevent conflicts during transition
   */
  static #dedupeIds(node) {
    const uniqueKey = Date.now();
    node.querySelectorAll('[id], [form]').forEach(element => {
      if (element.id) {
        element.id = `${element.id}-${uniqueKey}`;
      }
      if (element.form) {
        element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
      }
    });
  }

  /**
   * Sets inner HTML and reinjects script tags to allow execution
   */
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach(oldScriptTag => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach(attribute => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.quantityUpdateUnsubscriber = undefined;
    if (this.input) this.bindButtons();
  }

  bindButtons() {
    if (!this.input || this._qtyBound) return;
    this._qtyBound = true;
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  connectedCallback() {
    if (!this.input) this.input = this.querySelector('input');
    this.bindButtons();
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;
    const button = event.target.closest('button') || event.currentTarget;
    const name = button && button.name;

    if (name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    if (!this.input) return;
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      if (buttonMinus) buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      if (buttonPlus) buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

if (!customElements.get('quantity-input')) {
  customElements.define('quantity-input', QuantityInput);
}

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {

    
    this.querySelectorAll('summary').forEach((summary) => {
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    });
    
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();

    this.drawerElement = this.querySelector('#menu-drawer[data-drawer-src]');
    this.drawerSummary = this.mainDetailsToggle && this.mainDetailsToggle.querySelector(':scope > summary');
    this.drawerContentPromise = null;

    if (this.drawerElement && this.drawerSummary) {
      const prefetch = () => this.loadDrawerContent().catch(() => {});
      this.drawerSummary.addEventListener('pointerenter', prefetch, { once: true });
      this.drawerSummary.addEventListener('touchstart', prefetch, { once: true, passive: true });
      this.drawerSummary.addEventListener('focus', prefetch, { once: true });
      this.drawerSummary.addEventListener('click', this.onDeferredSummaryClick.bind(this), true);
    }
  }

  onDeferredSummaryClick(event) {
    if (
      !this.drawerElement ||
      !this.drawerElement.hasAttribute('data-drawer-src') ||
      this.mainDetailsToggle.hasAttribute('open')
    ) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    this.loadDrawerContent()
      .catch(() => {})
      .finally(() => {
        if (this.mainDetailsToggle.hasAttribute('open')) return;
        this.mainDetailsToggle.setAttribute('open', '');
        this.openMenuDrawer(this.drawerSummary);
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      });
  }

  loadDrawerContent() {
    const source = this.drawerElement && this.drawerElement.getAttribute('data-drawer-src');
    if (!source) return Promise.resolve();
    if (this.drawerContentPromise) return this.drawerContentPromise;

    const locale =
      document.documentElement.lang ||
      (window.Shopify && window.Shopify.locale) ||
      'default';
    const cacheKey = `fab.headerDrawer.v1:${locale}`;
    let cached = null;

    try {
      cached = sessionStorage.getItem(cacheKey);
    } catch (_) {}

    this.drawerContentPromise = (cached
      ? Promise.resolve(cached)
      : fetch(source, { credentials: 'same-origin' })
          .then((response) => {
            if (!response.ok) throw new Error(`Header drawer request failed: ${response.status}`);
            return response.text();
          })
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const payload = doc.querySelector('[data-header-drawer-content]');
            if (!payload) throw new Error('Header drawer response was missing its content');
            const content = payload.innerHTML;
            try {
              sessionStorage.setItem(cacheKey, content);
            } catch (_) {}
            return content;
          }))
      .then((content) => this.injectDrawerContent(content))
      .catch((error) => {
        this.drawerContentPromise = null;
        throw error;
      });

    return this.drawerContentPromise;
  }

  injectDrawerContent(content) {
    if (!this.drawerElement || !content) return;

    const imagePanel = this.drawerElement.querySelector('[data-mega-nav-panel]');
    const utilityLinks = this.drawerElement.querySelector('.menu-drawer__utility-links');

    if (imagePanel) imagePanel.remove();
    if (utilityLinks) utilityLinks.remove();

    this.drawerElement.innerHTML = content;

    const navigation = this.drawerElement.querySelector('.menu-drawer__navigation');
    const navigationContainer = this.drawerElement.querySelector('.menu-drawer__navigation-container');
    if (imagePanel && navigation) navigation.prepend(imagePanel);
    if (utilityLinks && navigationContainer) navigationContainer.append(utilityLinks);

    this.drawerElement.removeAttribute('data-drawer-src');
    this.bindDeferredDrawerEvents();

    if (typeof window.bindDrawerMegaNavImages === 'function') {
      window.bindDrawerMegaNavImages();
    }
    this.notifyDrawerInjected();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      trapFocus(this.mainDetailsToggle, this.drawerSummary);
    }
  }

  bindDeferredDrawerEvents() {
    this.drawerElement.querySelectorAll('summary').forEach((summary) => {
      const details = summary.parentElement;
      summary.setAttribute('role', 'button');
      summary.setAttribute('aria-expanded', details.hasAttribute('open'));
      if (summary.nextElementSibling && summary.nextElementSibling.id) {
        summary.setAttribute('aria-controls', summary.nextElementSibling.id);
      }
      summary.addEventListener('click', this.onSummaryClick.bind(this));
    });

    this.drawerElement
      .querySelectorAll(
        'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
      )
      .forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  notifyDrawerInjected(attempt = 0) {
    if (
      window.__fabTrackingHelpers &&
      typeof window.__fabTrackingHelpers.notifyInjected === 'function'
    ) {
      window.__fabTrackingHelpers.notifyInjected(this.drawerElement);
      return;
    }
    if (attempt < 20) {
      window.setTimeout(() => this.notifyDrawerInjected(attempt + 1), 100);
    }
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (this._bound) return;
    const button = this.querySelector('button');
    if (!button) return;
    this._bound = true;
    button.addEventListener('click', () => {
      const targetId = this.getAttribute('data-modal');
      const target = document.querySelector(targetId);
      if (target) {
        if (target.tagName === 'PRODUCT-DRAWER') {
          target.open(button);
        } else if (target.tagName === 'MODAL-DIALOG') {
          target.show(button);
        }
      }
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

if (typeof ON_CHANGE_DEBOUNCE_TIMER === 'undefined') {
  window.ON_CHANGE_DEBOUNCE_TIMER = 300; // Fallback value
}

function replaceCartUpsellSurfaces(html) {
  if (!html) return;
  function swap(selector, afterOverlay) {
    var incoming = html.querySelector(selector);
    var current = document.querySelector(selector);
    if (incoming && current) {
      current.replaceWith(incoming);
      return;
    }
    if (!incoming && current) {
      current.remove();
      return;
    }
    if (incoming && afterOverlay) {
      var overlay = document.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.insertAdjacentElement('afterend', incoming);
    }
  }
  swap('[data-fab-cart-upsell-desktop]', true);
}

function applyCartCountBubble(sectionHtml) {
  var root = document.getElementById('cart-icon-bubble');
  if (!root) return;
  root.querySelectorAll('style').forEach(function (el) {
    el.remove();
  });
  var next = null;
  if (sectionHtml) {
    next = new DOMParser().parseFromString(sectionHtml, 'text/html').querySelector('.cart-count-bubble');
  }
  var current = root.querySelector('.cart-count-bubble');
  if (next) {
    if (current) {
      current.innerHTML = next.innerHTML;
    } else {
      var host = root.querySelector('dynamic') || root;
      host.appendChild(document.importNode(next, true));
    }
    var bubble = root.querySelector('.cart-count-bubble');
    if (bubble) {
      bubble.classList.remove('pop');
      void bubble.offsetWidth;
      bubble.classList.add('pop');
      window.setTimeout(function () {
        bubble.classList.remove('pop');
      }, 300);
    }
  } else if (current) {
    current.remove();
  }
}

window.applyCartCountBubble = applyCartCountBubble;

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items' || event.source === 'product-form') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue === 0) {
      event.target.setCustomValidity('');
      this.updateQuantity(
        index,
        0,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
      return;
    }

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer&_=${Date.now()}`, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      })
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');

          // Atomically replace the entire cart drawer body (`.drawer__inner`)
          // so every state-dependent element refreshes together: empty-state,
          // cart-drawer-items, summary footer, fixed checkout footer, trust
          // strip, free-shipping bar. The previous code only swapped
          // `cart-drawer-items` (and a non-existent `.cart-drawer__footer`),
          // which left elements like `.cart-drawer__empty-state_is-empty` and
          // `.drawer__footer-fixed_is-empty` stuck with their first-render
          // class names — so on an empty → non-empty transition the empty
          // message kept showing above the items and the checkout button
          // stayed hidden. `#CartDrawer-Overlay` is a sibling of
          // `.drawer__inner`, so its click-to-close listener (attached in
          // cart-drawer.js's constructor) is preserved.
          const sourceInner = html.querySelector('.drawer__inner');
          const targetInner = document.querySelector('.drawer__inner');
          if (sourceInner && targetInner) {
            targetInner.replaceWith(sourceInner);
          }

          replaceCartUpsellSurfaces(html);

          // Sync the `is-empty` class on the <cart-drawer> wrapper from the
          // server's authoritative state, so wrapper-driven CSS rules
          // (e.g. `cart-drawer.is-empty .drawer__inner { ... }`) match.
          const sourceCartDrawer = html.querySelector('cart-drawer');
          const targetCartDrawer = document.querySelector('cart-drawer');
          if (sourceCartDrawer && targetCartDrawer) {
            targetCartDrawer.classList.toggle(
              'is-empty',
              sourceCartDrawer.classList.contains('is-empty')
            );
          }

          document.dispatchEvent(
            new CustomEvent('fab:cart-updated', {
              detail: { root: document.querySelector('cart-drawer') || document },
            })
          );
        })
        .catch((e) => {
          console.error(e);
          throw e;
        });
    }

    return fetch(`${routes.cart_url}?section_id=main-cart-items`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceQty = html.querySelector('cart-items');
        this.innerHTML = sourceQty.innerHTML;
      })
      .catch((e) => {
        console.error(e);
        throw e;
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const sectionHtml = parsedState.sections && parsedState.sections[section.section];
          if (section.id === 'cart-icon-bubble') {
            applyCartCountBubble(sectionHtml);
            return;
          }
          if (section.id === 'CartDrawer' && sectionHtml) {
            replaceCartUpsellSurfaces(new DOMParser().parseFromString(sectionHtml, 'text/html'));
          }
          const root = document.getElementById(section.id);
          if (!root || !sectionHtml) return;
          const elementToReplace = root.querySelector(section.selector) || root;
          try {
            elementToReplace.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
          } catch (_) {}
        });

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
        document.dispatchEvent(
          new CustomEvent('fab:cart-updated', {
            detail: { root: document.querySelector('cart-drawer') || document },
          })
        );

        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && quantityElement && updatedValue !== parseInt(quantityElement.value, 10)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && name && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          const empty = cartDrawerWrapper.querySelector('.drawer__inner-empty');
          const emptyFocus = empty && (empty.querySelector('a, button') || empty);
          if (empty && emptyFocus) trapFocus(empty, emptyFocus);
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    const errorText = lineItemError && lineItemError.querySelector('.cart-item__error-text');
    if (errorText) errorText.textContent = message;

    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    if (!cartStatus) return;
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

/**
 * Shared right-hand drawer: cart, size guide, framing info, collection filters.
 *
 * Host must use class "drawer" (cart chrome). Open/close, overlay click,
 * Escape, focus trap, and body scroll lock live here so every surface stays
 * in lockstep when the pattern changes.
 */
class FabDrawer extends HTMLElement {
  constructor() {
    super();
    this.activeElement = null;
    this._onKeyUp = (evt) => {
      if (evt.code === 'Escape') this.close();
    };
    this.addEventListener('keyup', this._onKeyUp);
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-drawer-close], .drawer__close, .product-drawer__close')) {
        event.preventDefault();
        this.close();
        return;
      }
      if (
        event.target === this ||
        event.target.matches('[data-drawer-overlay], .cart-drawer__overlay, .drawer__overlay, .product-drawer__overlay')
      ) {
        this.close();
      }
    });
  }

  connectedCallback() {
    if (this.hasAttribute('data-drawer-no-portal')) return;
    if (this._fabDrawerMoved) return;
    this._fabDrawerMoved = true;
    if (this.parentElement !== document.body) {
      document.body.appendChild(this);
    }
  }

  get trapContainer() {
    return this.querySelector('[role="dialog"]') || this.querySelector('.drawer__inner') || this;
  }

  get trapFocusEl() {
    return (
      this.querySelector('[data-drawer-initial-focus]') ||
      this.querySelector('.drawer__close') ||
      this.querySelector('.drawer__heading') ||
      this.trapContainer
    );
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const container = this.trapContainer;
        const focusEl = this.trapFocusEl;
        if (container && typeof trapFocus === 'function') {
          trapFocus(container, focusEl);
        } else if (focusEl && focusEl.focus) {
          focusEl.focus();
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }
    document.body.classList.remove('overflow-hidden');

    if (this.activeElement && typeof this.activeElement.focus === 'function') {
      try {
        this.activeElement.focus();
      } catch (e) {}
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('fab-drawer')) {
  customElements.define('fab-drawer', FabDrawer);
}

window.FabDrawer = FabDrawer;

class CartDrawer extends FabDrawer {
  constructor() {
    super();
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    // Cart is already portalled from layout; do not move it.
  }

  get trapContainer() {
    return this.classList.contains('is-empty')
      ? this.querySelector('.drawer__inner-empty')
      : document.getElementById('CartDrawer');
  }

  get trapFocusEl() {
    return this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (window.FabCommerceAssets && typeof window.FabCommerceAssets.load === 'function') {
      window.FabCommerceAssets.load().catch(() => {});
    }
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    super.open(triggeredBy);
  }

  close() {
    this.classList.remove('active');
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }
    document.body.classList.remove('overflow-hidden');

    const cartIcon = document.querySelector('#cart-icon-bubble');
    if (cartIcon) {
      cartIcon.blur();
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      if (section.id === 'cart-icon-bubble') {
        applyCartCountBubble(parsedState.sections[section.id]);
      } else if (section.id === 'cart-drawer') {
        const html = new DOMParser().parseFromString(parsedState.sections[section.id], 'text/html');
        if (typeof replaceFabOfferSurfaces === 'function') replaceFabOfferSurfaces(html);
        const sourceInner = html.querySelector('.drawer__inner');
        const targetInner = this.querySelector('.drawer__inner');
        if (sourceInner && targetInner) targetInner.replaceWith(sourceInner);
        const sourceCartDrawer = html.querySelector('cart-drawer');
        if (sourceCartDrawer) {
          this.classList.toggle('is-empty', sourceCartDrawer.classList.contains('is-empty'));
        }
      } else {
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
    });

    if (!this.classList.contains('active')) this.open();
    document.dispatchEvent(new CustomEvent('fab:cart-updated', { detail: { root: this } }));
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

if (!customElements.get('localization-form')) {
  customElements.define(
    'localization-form',
    class LocalizationForm extends HTMLElement {
      constructor() {
        super();
        this.mql = window.matchMedia('(min-width: 750px)');
        this.header = document.querySelector('.header-wrapper');
        this.elements = {
          input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
          button: this.querySelector('button.localization-form__select'),
          panel: this.querySelector('.disclosure__list-wrapper'),
          search: this.querySelector('input[name="country_filter"]'),
          closeButton: this.querySelector('.country-selector__close-button'),
          resetButton: this.querySelector('.country-filter__reset-button'),
          searchIcon: this.querySelector('.country-filter__search-icon'),
          liveRegion: this.querySelector('#sr-country-search-results'),
        };
        this.addEventListener('keyup', this.onContainerKeyUp.bind(this));
        this.addEventListener('keydown', this.onContainerKeyDown.bind(this));
        this.addEventListener('focusout', this.closeSelector.bind(this));
        this.elements.button.addEventListener('click', this.openSelector.bind(this));

        if (this.elements.search) {
          this.elements.search.addEventListener('keyup', this.filterCountries.bind(this));
          this.elements.search.addEventListener('focus', this.onSearchFocus.bind(this));
          this.elements.search.addEventListener('blur', this.onSearchBlur.bind(this));
          this.elements.search.addEventListener('keydown', this.onSearchKeyDown.bind(this));
        }
        if (this.elements.closeButton) {
          this.elements.closeButton.addEventListener('click', this.hidePanel.bind(this));
        }
        if (this.elements.resetButton) {
          this.elements.resetButton.addEventListener('click', this.resetFilter.bind(this));
          this.elements.resetButton.addEventListener('mousedown', (event) => event.preventDefault());
        }

        this.querySelectorAll('a').forEach((item) => item.addEventListener('click', this.onItemClick.bind(this)));
      }

      hidePanel() {
        this.elements.button.setAttribute('aria-expanded', 'false');
        this.elements.panel.setAttribute('hidden', true);
        if (this.elements.search) {
          this.elements.search.value = '';
          this.filterCountries();
          this.elements.search.setAttribute('aria-activedescendant', '');
        }
        document.body.classList.remove('overflow-hidden-mobile');
        // The drawer and sticky header only exist when the header renders a menu,
        // so a footer-mounted selector has to tolerate their absence.
        const menuDrawer = document.querySelector('.menu-drawer');
        if (menuDrawer) menuDrawer.classList.remove('country-selector-open');
        if (this.header) this.header.preventHide = false;
      }

      onContainerKeyDown(event) {
        const focusableItems = Array.from(this.querySelectorAll('a')).filter(
          (item) => !item.parentElement.classList.contains('hidden')
        );
        let focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
        let itemToFocus;

        switch (event.code.toUpperCase()) {
          case 'ARROWUP':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex > 0 ? focusableItems[focusedItemIndex - 1] : focusableItems[focusableItems.length - 1];
            itemToFocus.focus();
            break;
          case 'ARROWDOWN':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex < focusableItems.length - 1 ? focusableItems[focusedItemIndex + 1] : focusableItems[0];
            itemToFocus.focus();
            break;
        }

        if (!this.elements.search) return;

        setTimeout(() => {
          focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
          if (focusedItemIndex > -1) {
            this.elements.search.setAttribute('aria-activedescendant', focusableItems[focusedItemIndex].id);
          } else {
            this.elements.search.setAttribute('aria-activedescendant', '');
          }
        });
      }

      onContainerKeyUp(event) {
        event.preventDefault();

        switch (event.code.toUpperCase()) {
          case 'ESCAPE':
            if (this.elements.button.getAttribute('aria-expanded') == 'false') return;
            this.hidePanel();
            event.stopPropagation();
            this.elements.button.focus();
            break;
          case 'SPACE':
            if (this.elements.button.getAttribute('aria-expanded') == 'true') return;
            this.openSelector();
            break;
        }
      }

      onItemClick(event) {
        event.preventDefault();
        const form = this.querySelector('form');
        this.elements.input.value = event.currentTarget.dataset.value;
        if (form) form.submit();
      }

      openSelector() {
        this.elements.button.focus();
        this.elements.panel.toggleAttribute('hidden');
        this.elements.button.setAttribute(
          'aria-expanded',
          (this.elements.button.getAttribute('aria-expanded') === 'false').toString()
        );
        if (!document.body.classList.contains('overflow-hidden-tablet')) {
          document.body.classList.add('overflow-hidden-mobile');
        }
        if (this.elements.search && this.mql.matches) {
          this.elements.search.focus();
        }
        if (this.hasAttribute('data-prevent-hide') && this.header) {
          this.header.preventHide = true;
        }
        const menuDrawer = document.querySelector('.menu-drawer');
        if (menuDrawer) menuDrawer.classList.add('country-selector-open');
      }

      closeSelector(event) {
        if (
          event.target.classList.contains('country-selector__overlay') ||
          !this.contains(event.target) ||
          !this.contains(event.relatedTarget)
        ) {
          this.hidePanel();
        }
      }

      normalizeString(str) {
        return str
          .normalize('NFD')
          .replace(/\p{Diacritic}/gu, '')
          .toLowerCase();
      }

      filterCountries() {
        const searchValue = this.normalizeString(this.elements.search.value);
        const popularCountries = this.querySelector('.popular-countries');
        const allCountries = this.querySelectorAll('a');
        let visibleCountries = allCountries.length;

        this.elements.resetButton.classList.toggle('hidden', !searchValue);

        if (popularCountries) {
          popularCountries.classList.toggle('hidden', searchValue);
        }

        allCountries.forEach((item) => {
          const countryName = this.normalizeString(item.querySelector('.country').textContent);
          if (countryName.indexOf(searchValue) > -1) {
            item.parentElement.classList.remove('hidden');
            visibleCountries++;
          } else {
            item.parentElement.classList.add('hidden');
            visibleCountries--;
          }
        });

        const searchCountTemplate =
          window.accessibilityStrings && window.accessibilityStrings.countrySelectorSearchCount;
        if (this.elements.liveRegion && searchCountTemplate) {
          this.elements.liveRegion.textContent = searchCountTemplate.replace('[count]', visibleCountries);
        }

        this.querySelector('.country-selector').scrollTop = 0;
        this.querySelector('.country-selector__list').scrollTop = 0;
      }

      resetFilter(event) {
        event.stopPropagation();
        this.elements.search.value = '';
        this.filterCountries();
        this.elements.search.focus();
      }

      onSearchFocus() {
        this.elements.searchIcon.classList.add('country-filter__search-icon--hidden');
      }

      onSearchBlur() {
        if (!this.elements.search.value) {
          this.elements.searchIcon.classList.remove('country-filter__search-icon--hidden');
        }
      }

      onSearchKeyDown(event) {
        if (event.code.toUpperCase() === 'ENTER') {
          event.preventDefault();
        }
      }
    }
  );
}

class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);

class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');
    this._closing = false;
    this._morphGen = 0;

    this.detailsContainer.addEventListener('keyup', (event) => event.code.toUpperCase() === 'ESCAPE' && this.close());
    this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this));
    const closeButton = this.querySelector('.modal__close-button, .search-modal__close-button');
    if (closeButton) closeButton.addEventListener('click', this.close.bind(this));

    this.summaryToggle.setAttribute('role', 'button');
  }

  isOpen() {
    return this.detailsContainer.hasAttribute('open');
  }

  onSummaryClick(event) {
    event.preventDefault();
    event.target.closest('details').hasAttribute('open') ? this.close() : this.open(event);
  }

  onBodyClick(event) {
    if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) this.close(false);
  }

  headerEl() {
    return this.closest('.header');
  }

  shouldMorph() {
    return !!(
      this.classList.contains('header__search') &&
      this.headerEl() &&
      this.headerEl().classList.contains('header--search-bar') &&
      document.querySelector('[data-header-search-trigger]') &&
      window.matchMedia('(min-width: 990px)').matches
    );
  }

  reduceMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  morphField() {
    return this.querySelector('.search-modal .field');
  }

  morphButtons(field) {
    return field ? field.querySelectorAll('.field__button, search-visual-menu') : [];
  }

  triggerRect() {
    const trigger = document.querySelector('[data-header-search-trigger]');
    return trigger ? trigger.getBoundingClientRect() : null;
  }

  invertFromTrigger(field, first) {
    const last = field.getBoundingClientRect();
    if (!first || !last.width || !last.height) return null;
    const sx = first.width / last.width;
    const sy = first.height / last.height;
    if (!isFinite(sx) || !isFinite(sy) || sx <= 0 || sy <= 0) return null;
    return {
      transform: `translate(${Math.round(first.left - last.left)}px, ${Math.round(first.top - last.top)}px) scale(${sx}, ${sy})`,
      inverse: `translateY(-50%) scale(${1 / sx}, ${1 / sy})`,
    };
  }

  clearMorphStyles(field) {
    if (!field) return;
    field.classList.remove('is-morphing');
    field.style.transition = '';
    field.style.transform = '';
    field.style.willChange = '';
    this.morphButtons(field).forEach((button) => {
      button.style.transition = '';
      button.style.transform = '';
    });
  }

  onMorphEnd(field, gen, handler) {
    let finished = false;
    const done = (event) => {
      if (finished) return;
      if (event && event.target !== field) return;
      if (event && event.propertyName && event.propertyName !== 'transform') return;
      finished = true;
      field.removeEventListener('transitionend', done);
      if (gen !== this._morphGen) return;
      handler();
    };
    field.addEventListener('transitionend', done);
    setTimeout(() => done(), 400);
  }

  playOpenMorph(first) {
    const field = this.morphField();
    const inverted = field && first ? this.invertFromTrigger(field, first) : null;
    if (!field || !inverted) return;

    const gen = this._morphGen;
    const ease = 'transform 320ms cubic-bezier(0.16, 1, 0.3, 1)';
    field.classList.add('is-morphing');
    field.style.transformOrigin = 'top left';
    field.style.transition = 'none';
    field.style.transform = inverted.transform;
    field.style.willChange = 'transform';
    this.morphButtons(field).forEach((button) => {
      button.style.transition = 'none';
      button.style.transform = inverted.inverse;
    });

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (gen !== this._morphGen) return;
        field.style.transition = ease;
        field.style.transform = 'translate(0) scale(1)';
        this.morphButtons(field).forEach((button) => {
          button.style.transition = ease;
          button.style.transform = 'translateY(-50%) scale(1)';
        });
        this.onMorphEnd(field, gen, () => {
          if (gen !== this._morphGen) return;
          this.clearMorphStyles(field);
        });
      });
    });
  }

  playCloseMorph(done) {
    const field = this.morphField();
    const first = this.triggerRect();
    const inverted = field && first ? this.invertFromTrigger(field, first) : null;
    this.detailsContainer.classList.add('is-search-closing');

    if (!field || !inverted) {
      done();
      return;
    }

    const gen = this._morphGen;
    const ease = 'transform 200ms cubic-bezier(0.16, 1, 0.3, 1)';
    field.classList.add('is-morphing');
    field.style.transformOrigin = 'top left';
    field.style.willChange = 'transform';
    field.style.transition = ease;
    field.style.transform = inverted.transform;
    this.morphButtons(field).forEach((button) => {
      button.style.transition = ease;
      button.style.transform = inverted.inverse;
    });

    this.onMorphEnd(field, gen, done);
  }

  open(event) {
    if (this._closing) return;
    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
    this._morphGen += 1;
    const morph = this.shouldMorph() && !this.reduceMotion();
    const first = morph ? this.triggerRect() : null;

    this.detailsContainer.setAttribute('open', true);
    document.body.addEventListener('click', this.onBodyClickEvent);
    document.body.classList.add('overflow-hidden');

    if (morph && first) this.playOpenMorph(first);

    trapFocus(
      this.detailsContainer.querySelector('[tabindex="-1"]'),
      this.detailsContainer.querySelector('input:not([type="hidden"])')
    );

    if (this.classList.contains('header__search')) {
      try {
        if (window.__fabTrackingHelpers && typeof window.__fabTrackingHelpers.capture === 'function') {
          window.__fabTrackingHelpers.capture('search_opened', {
            page_type: document.body?.dataset?.pageType || null,
            page_path: window.location.pathname || null,
            entry_point: this.dataset.entryPoint || 'header_icon'
          });
        }
        window.__fabPredictiveShownQueries = new Set();
      } catch (e) {}
    }
  }

  finishClose(focusToggle) {
    this.detailsContainer.classList.remove('is-search-closing');
    removeTrapFocus(focusToggle ? this.summaryToggle : null);
    this.detailsContainer.removeAttribute('open');
    this.clearMorphStyles(this.morphField());
    document.body.removeEventListener('click', this.onBodyClickEvent);
    document.body.classList.remove('overflow-hidden');
    this._closing = false;
  }

  close(focusToggle = true) {
    if (!this.isOpen() || this._closing) return;
    if (this.shouldMorph() && !this.reduceMotion()) {
      this._closing = true;
      this._morphGen += 1;
      this.playCloseMorph(() => this.finishClose(focusToggle));
      return;
    }
    this.finishClose(focusToggle);
  }
}

customElements.define('details-modal', DetailsModal);

class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');

    if (this.input) {
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
    }
  }

  toggleResetButton() {
    if (!this.resetButton) return;
    const resetIsHidden = this.resetButton.classList.contains('hidden');
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden');
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add('hidden');
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();
    // Don't reset if the user has selected an element on the predictive search dropdown
    if (this.shouldResetForm()) {
      this.input.value = '';
      this.input.focus();
      this.toggleResetButton();
    }
  }
}

customElements.define('search-form', SearchForm);

function fabPredictiveCapture(eventName, props) {
  try {
    if (window.__fabTrackingHelpers && typeof window.__fabTrackingHelpers.capture === 'function') {
      window.__fabTrackingHelpers.capture(eventName, props || {});
    }
  } catch (e) {}
}

const FAB_RECENT_SEARCHES_KEY = 'fab_recent_searches';
const FAB_RECENT_SEARCHES_CAP = 8;

function fabReadRecentSearches() {
  try {
    const raw = window.localStorage.getItem(FAB_RECENT_SEARCHES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
}

function fabWriteRecentSearch(query) {
  const q = (query || '').trim();
  if (q.length < 2) return;
  try {
    const next = fabReadRecentSearches().filter(function (item) {
      return item && String(item.q || '').toLowerCase() !== q.toLowerCase();
    });
    next.unshift({ q: q, t: Date.now() });
    window.localStorage.setItem(FAB_RECENT_SEARCHES_KEY, JSON.stringify(next.slice(0, FAB_RECENT_SEARCHES_CAP)));
  } catch (e) {}
}

function fabClearRecentSearches() {
  try {
    window.localStorage.removeItem(FAB_RECENT_SEARCHES_KEY);
  } catch (e) {}
}

function fabRecentSearchUrl(searchUrl, query) {
  const base = searchUrl || '/search';
  const join = base.indexOf('?') >= 0 ? '&' : '?';
  return base + join + 'q=' + encodeURIComponent(query) + '&options%5Bprefix%5D=last';
}

const SEARCH_ZERO_STATE = {
  html: null,
  promise: null,
  failed: false,
  injected: false,
};

function getSearchZeroStateConfig() {
  const el = document.querySelector('[data-search-zero-state-config]');
  if (!el) return { popular_searches: '', collection_handles: '' };
  try {
    const data = JSON.parse(el.textContent);
    return {
      popular_searches: data.popular_searches || '',
      collection_handles: data.collection_handles || '',
    };
  } catch (e) {
    return { popular_searches: '', collection_handles: '' };
  }
}

function searchZeroStateUrls() {
  const config = getSearchZeroStateConfig();
  const extra = ['view=search-zero-state'];
  if (config.collection_handles) {
    extra.push('szs_handles=' + encodeURIComponent(config.collection_handles));
  }
  const qs = extra.join('&');
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const searchPath = root.replace(/\/?$/, '/') + 'search';
  return [window.location.pathname + '?' + qs, searchPath + '?' + qs];
}

function parseSearchZeroStatePayload(text) {
  const doc = new DOMParser().parseFromString(text, 'text/html');
  const payload = doc.querySelector('[data-search-zero-state-payload]');
  if (!payload) return null;
  return payload.querySelector('search-zero-state') || payload;
}

function graftSearchZeroStateBrowse(sourceRoot) {
  const source =
    sourceRoot.matches && sourceRoot.matches('search-zero-state')
      ? sourceRoot
      : sourceRoot.querySelector('search-zero-state');
  if (!source) return false;
  const browse = source.querySelector('[data-browse-block]');
  if (!browse) return false;
  const hosts = document.querySelectorAll('search-zero-state');
  if (!hosts.length) return false;

  hosts.forEach((host) => {
    if (host.querySelector('[data-browse-block]')) return;
    const clone = browse.cloneNode(true);
    let columns = host.querySelector('.search-zero-state__columns');
    if (!columns) {
      columns = document.createElement('div');
      columns.className = 'search-zero-state__columns';
      const template = host.querySelector('[data-recent-row]');
      host.insertBefore(columns, template);
    }
    columns.appendChild(clone);
  });

  const count = source.getAttribute('data-collection-count');
  const browseSource = source.getAttribute('data-browse-source');
  hosts.forEach((host) => {
    if (count) host.setAttribute('data-collection-count', count);
    if (browseSource) host.setAttribute('data-browse-source', browseSource);
  });

  SEARCH_ZERO_STATE.injected = true;

  try {
    if (window.__fabTrackingHelpers && typeof window.__fabTrackingHelpers.notifyInjected === 'function') {
      hosts.forEach((host) => window.__fabTrackingHelpers.notifyInjected(host));
    }
  } catch (e) {}

  document.querySelectorAll('predictive-search').forEach((host) => {
    if (
      host.hasAttribute('open') &&
      typeof host.getQuery === 'function' &&
      !host.getQuery().length &&
      typeof host.openZeroState === 'function'
    ) {
      host.openZeroState();
    }
  });
  return true;
}

function ensureSearchZeroState() {
  if (SEARCH_ZERO_STATE.failed || SEARCH_ZERO_STATE.injected) return Promise.resolve();
  if (!document.querySelector('search-zero-state')) return Promise.resolve();
  if (SEARCH_ZERO_STATE.html) {
    const doc = new DOMParser().parseFromString(SEARCH_ZERO_STATE.html, 'text/html');
    graftSearchZeroStateBrowse(doc);
    return Promise.resolve();
  }
  if (SEARCH_ZERO_STATE.promise) return SEARCH_ZERO_STATE.promise;

  const urls = searchZeroStateUrls();
  SEARCH_ZERO_STATE.promise = (function fetchNext(index) {
    if (index >= urls.length) return Promise.reject(new Error('zero-state missing'));
    return fetch(urls[index])
      .then((res) => {
        if (!res.ok) throw new Error('zero-state ' + res.status);
        return res.text();
      })
      .then((text) => {
        const node = parseSearchZeroStatePayload(text);
        if (!node) throw new Error('zero-state payload missing');
        SEARCH_ZERO_STATE.html = node.outerHTML;
        if (!graftSearchZeroStateBrowse(node)) throw new Error('zero-state browse missing');
      })
      .catch((error) => {
        if (index + 1 < urls.length) return fetchNext(index + 1);
        throw error;
      });
  })(0).catch(() => {
    SEARCH_ZERO_STATE.failed = true;
  });

  return SEARCH_ZERO_STATE.promise;
}

// --- PredictiveSearch class from predictive-search.js ---
class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this._suggestStartedAt = 0;
    this._lastSuggestQuery = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    if (!this.input || !this.input.form) return;
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('input', this.onQueryInput.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    // Results HTML is replaced every render — delegate clicks once on the host.
    this.addEventListener('click', this.onSuggestionClick.bind(this));
  }

  getQuery() {
    return this.input ? this.input.value.trim() : '';
  }

  onQueryInput() {
    if (this.getQuery().length) {
      this.setAttribute('data-querying', '');
    } else {
      this.removeAttribute('data-querying');
    }
  }

  syncHeaderExpanded(expanded) {
    const header = this.closest('.header');
    if (!header || !header.classList.contains('header--search-bar')) return;
    header.classList.toggle('header--search-expanded', expanded);
  }

  onChange() {
    super.onChange();
    if (this._suppressOpen) return;
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;
    if (!this.searchTerm.length) {
      this.openZeroState();
      return;
    }
    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    const selectedLink = this.querySelector('[aria-selected="true"] a');
    if (!this.getQuery().length || selectedLink) {
      event.preventDefault();
      return;
    }
    fabWriteRecentSearch(this.getQuery());
    // Stash source for the results page — one search_performed with full counts
    // (firing here too double-counted every header submit).
    try {
      sessionStorage.setItem(
        'fab_search_source',
        JSON.stringify({ source: 'predictive_submit', query: this.getQuery(), t: Date.now() })
      );
    } catch (e) {}
  }

  onSuggestionClick(event) {
    const link = event.target && event.target.closest
      ? event.target.closest('[data-suggestion-type]')
      : null;
    if (!link || !this.contains(link)) return;

    const type = link.dataset.suggestionType || null;
    const position = Number.parseInt(link.dataset.suggestionPosition || '', 10);
    const group = link.closest('.predictive-search__results-list');
    let groupPosition = position;
    if (group) {
      const siblings = Array.from(group.querySelectorAll('[data-suggestion-type="' + type + '"]'));
      const idx = siblings.indexOf(link);
      if (idx >= 0) groupPosition = idx + 1;
    }

    fabPredictiveCapture('search_suggestion_clicked', {
      query: this.getQuery() || this._lastSuggestQuery || '',
      suggestion_type: type,
      target_handle: link.dataset.suggestionHandle || null,
      position: Number.isFinite(position) ? position : null,
      group_position: Number.isFinite(groupPosition) ? groupPosition : null
    });

    if (type === 'query_completion') {
      const labelled = link.querySelector('[aria-label]');
      fabWriteRecentSearch(
        (labelled && labelled.getAttribute('aria-label')) || this.getQuery() || this._lastSuggestQuery
      );
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.openZeroState();
    }
  }

  onFocus() {
    if (this._suppressOpen) return;
    const currentSearchTerm = this.getQuery();
    this.syncHeaderExpanded(true);
    if (!currentSearchTerm.length) {
      this.openZeroState();
      return;
    }
    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (this._suppressOpen) return;
      const active = document.activeElement;
      if (this.contains(active)) return;
      if (active && active.closest && active.closest('[data-header-search-cancel]')) return;
      this.close();
    });
  }

  onKeyup(event) {
    if (this._suppressOpen) return;
    if (!this.getQuery().length) {
      this.openZeroState();
      return;
    }
    event.preventDefault();
    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (!currentButtonText || !previousTerm) return;
    const escaped = previousTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const matches = currentButtonText.match(new RegExp(escaped, 'g'));
    if (!matches || matches.length !== 1) return;
    searchForTextElement.innerText = currentButtonText.replace(previousTerm, newTerm);
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;
    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;
    if (moveUp && !selectedElement) return;
    let selectedElementIndex = -1;
    let i = 0;
    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }
    this.statusElement.textContent = '';
    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }
    if (activeElementIndex === selectedElementIndex) return;
    const activeElement = allVisibleElements[activeElementIndex];
    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();
    this._lastSuggestQuery = searchTerm;
    this._suggestStartedAt = performance.now();
    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey], { fromCache: true });
      return;
    }
    const root = window.Shopify?.routes?.root || '/'; // e.g. "/de-de/"
    const url = new URL(`${root.replace(/\/?$/, '/') }search/suggest`, window.location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('section_id', 'predictive-search');
    url.searchParams.set('resources[type]', 'query,product,collection');
    url.searchParams.set('resources[limit]', '4');
    url.searchParams.set('resources[limit_scope]', 'each');
    
    fetch(url.toString(), {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup, { fromCache: false });
      })
      .catch((error) => {
        if (error?.code === 20) {
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;
    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup, opts) {
    opts = opts || {};
    const latencyMs = this._suggestStartedAt
      ? Math.round(performance.now() - this._suggestStartedAt)
      : null;
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.open();
    this.trackSuggestionsShown(latencyMs, !!opts.fromCache);
  }

  trackSuggestionsShown(latencyMs, fromCache) {
    const root = this.predictiveSearchResults || this;
    const metaNode = root.querySelector('[data-fab-predictive-meta]') || root.querySelector('#predictive-search-results');

    // Prefer data-* on the results root (survives innerHTML). Fall back to
    // counting rendered suggestion links if attributes are missing.
    const attrCount = function (name) {
      if (!metaNode) return null;
      const raw = metaNode.getAttribute(name);
      if (raw == null || raw === '') return null;
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    };
    const domCount = function (type) {
      return root.querySelectorAll('[data-suggestion-type="' + type + '"]').length;
    };

    const query =
      (metaNode && metaNode.getAttribute('data-query')) ||
      this._lastSuggestQuery ||
      this.getQuery();
    if (!query) return;

    window.__fabPredictiveShownQueries = window.__fabPredictiveShownQueries || new Set();
    if (window.__fabPredictiveShownQueries.has(query)) return;
    window.__fabPredictiveShownQueries.add(query);

    const productCount = attrCount('data-product-count') != null ? attrCount('data-product-count') : domCount('product');
    const collectionCount =
      attrCount('data-collection-count') != null ? attrCount('data-collection-count') : domCount('collection');
    const pageCount = attrCount('data-page-count') != null ? attrCount('data-page-count') : domCount('page');
    const articleCount =
      attrCount('data-article-count') != null ? attrCount('data-article-count') : domCount('article');
    const queryCompletionCount =
      attrCount('data-query-completion-count') != null
        ? attrCount('data-query-completion-count')
        : domCount('query_completion');
    const total = productCount + collectionCount + pageCount + articleCount + queryCompletionCount;

    fabPredictiveCapture('search_suggestions_shown', {
      query: query,
      product_count: productCount,
      collection_count: collectionCount,
      page_count: pageCount,
      article_count: articleCount,
      zero_suggestions: total === 0,
      latency_ms: fromCache ? 0 : latencyMs
    });
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const liveRegionCount = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (liveRegionCount) {
      this.setLiveRegionText(liveRegionCount.textContent);
    }
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    }
    this.markOpened();
  }

  openZeroState() {
    if (this._suppressOpen) return;
    this.removeAttribute('results');
    this.removeAttribute('loading');
    this.removeAttribute('data-querying');
    this.syncHeaderExpanded(true);
    if (this.predictiveSearchResults) this.predictiveSearchResults.removeAttribute('style');
    ensureSearchZeroState();
    const zero = this.querySelector('search-zero-state');
    if (!zero) {
      this.markOpened();
      return;
    }
    if (typeof zero.refreshRecents === 'function') zero.refreshRecents();
    if (typeof zero.hasContent === 'function' && !zero.hasContent()) {
      if (SEARCH_ZERO_STATE.promise && !SEARCH_ZERO_STATE.failed) {
        this.markOpened();
        return;
      }
      this.close();
      return;
    }
    this.markOpened();
    this.trackZeroStateShown();
  }

  markOpened() {
    const wasOpen = this.isOpen;
    this.setAttribute('open', true);
    if (this.input) this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
    if (wasOpen) return;
    this._zeroStateShownThisOpen = false;
    if (this.closest('details-modal')) return;
    fabPredictiveCapture('search_opened', {
      page_type: document.body?.dataset?.pageType || null,
      page_path: window.location.pathname || null,
      entry_point: this.dataset.entryPoint || 'header_bar_inline'
    });
    window.__fabPredictiveShownQueries = new Set();
  }

  trackZeroStateShown() {
    if (this.getAttribute('results') === 'true') return;
    if (this._zeroStateShownThisOpen) return;
    const zero = this.querySelector('search-zero-state');
    if (!zero || typeof zero.hasContent !== 'function' || !zero.hasContent()) return;
    this._zeroStateShownThisOpen = true;
    const entry =
      this.dataset.entryPoint ||
      this.closest('details-modal')?.dataset?.entryPoint ||
      'header_icon';
    if (typeof zero.trackShown === 'function') zero.trackShown(entry);
  }

  closeFromCancel() {
    this._suppressOpen = true;
    this.searchTerm = '';
    this.abortController.abort();
    this.abortController = new AbortController();
    const header = this.closest('.header');
    if (header) {
      header.classList.add('header--search-closing');
      header.classList.remove('header--search-expanded');
    }
    this.close(true);
    if (this.input) this.input.blur();
    clearTimeout(this._suppressOpenTimer);
    this._suppressOpenTimer = setTimeout(() => {
      this._suppressOpen = false;
      if (header) header.classList.remove('header--search-closing');
    }, 800);
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
    this._zeroStateShownThisOpen = false;
    if (!this.contains(document.activeElement)) this.syncHeaderExpanded(false);
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);
    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.removeAttribute('data-querying');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    if (this.predictiveSearchResults) this.predictiveSearchResults.removeAttribute('style');
  }
}

class SearchZeroState extends HTMLElement {
  connectedCallback() {
    this.searchUrl = this.getAttribute('data-search-url') || '/search';
  }

  ensureContent() {
    if (this._contentReady) return;
    this._contentReady = true;

    const recentBlock = document.createElement('div');
    recentBlock.className = 'search-zero-state__block';
    recentBlock.dataset.recentBlock = '';
    recentBlock.hidden = true;
    const recentHead = document.createElement('div');
    recentHead.className = 'search-zero-state__head';
    const recentHeading = document.createElement('p');
    recentHeading.className = 'search-zero-state__heading';
    recentHeading.textContent = this.dataset.recentLabel || 'Recent searches';
    const clearButton = document.createElement('button');
    clearButton.type = 'button';
    clearButton.className = 'search-zero-state__clear';
    clearButton.dataset.recentClear = '';
    clearButton.textContent = this.dataset.clearRecentLabel || 'Clear';
    const recentList = document.createElement('div');
    recentList.className = 'search-zero-state__recents';
    recentList.dataset.recentList = '';
    recentHead.append(recentHeading, clearButton);
    recentBlock.append(recentHead, recentList);
    this.appendChild(recentBlock);

    const popularTerms = String(this.dataset.popularSearches || '')
      .split(',')
      .map((term) => term.trim())
      .filter(Boolean)
      .slice(0, 6);
    if (popularTerms.length) {
      const columns = document.createElement('div');
      columns.className = 'search-zero-state__columns';
      const popularBlock = document.createElement('div');
      popularBlock.className = 'search-zero-state__block';
      popularBlock.dataset.popularBlock = '';
      const popularHeading = document.createElement('p');
      popularHeading.className = 'search-zero-state__heading';
      popularHeading.textContent = this.dataset.popularLabel || 'Popular searches';
      const chips = document.createElement('div');
      chips.className = 'search-zero-state__chips';
      popularTerms.forEach((term, index) => {
        const link = document.createElement('a');
        link.href = fabRecentSearchUrl(this.searchUrl, term);
        link.className = 'search-zero-state__chip';
        link.dataset.track = 'click';
        link.dataset.targetType = 'popular_query';
        link.dataset.targetId = term.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
        link.dataset.slot = String(index);
        link.textContent = term;
        chips.appendChild(link);
      });
      popularBlock.append(popularHeading, chips);
      columns.appendChild(popularBlock);
      this.appendChild(columns);
    }

    this.list = this.querySelector('[data-recent-list]');
    this.block = this.querySelector('[data-recent-block]');
    clearButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        fabClearRecentSearches();
        this.refreshRecents();
        const input = this.closest('predictive-search')?.querySelector('input[type="search"]');
        if (input) input.focus();
    });
  }

  hasContent() {
    this.ensureContent();
    const recentsVisible = this.block && !this.block.hasAttribute('hidden');
    return Boolean(
      recentsVisible || this.querySelector('[data-popular-block]') || this.querySelector('[data-browse-block]')
    );
  }

  refreshRecents() {
    this.ensureContent();
    if (!this.list || !this.block) return;
    const recents = fabReadRecentSearches().slice(0, 5);
    this.list.replaceChildren();
    if (!recents.length) {
      this.block.setAttribute('hidden', '');
      return;
    }
    this.block.removeAttribute('hidden');
    recents.forEach((item, index) => {
      const q = item && item.q ? String(item.q) : '';
      if (!q) return;
      const node = document.createElement('a');
      node.className = 'search-zero-state__recent';
      node.href = fabRecentSearchUrl(this.searchUrl, q);
      node.dataset.track = 'click';
      node.dataset.targetType = 'recent_query';
      node.dataset.targetId = q.toLowerCase();
      node.dataset.slot = String(index);
      const icon = document.createElement('span');
      icon.className = 'search-zero-state__recent-icon';
      icon.setAttribute('aria-hidden', 'true');
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('width', '24');
      svg.setAttribute('height', '24');
      const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
      use.setAttribute('href', (this.dataset.iconSprite || '') + '#icon-search');
      svg.appendChild(use);
      icon.appendChild(svg);
      const label = document.createElement('span');
      label.className = 'search-zero-state__recent-q';
      label.textContent = q;
      node.append(icon, label);
      this.list.appendChild(node);
    });
  }

  trackShown(entryPoint) {
    this.ensureContent();
    const recents = this.block && !this.block.hasAttribute('hidden')
      ? this.list.querySelectorAll('.search-zero-state__recent').length
      : 0;
    fabPredictiveCapture('search_zero_state_shown', {
      recent_count: recents,
      popular_count: Number.parseInt(this.getAttribute('data-popular-count') || '0', 10) || 0,
      collection_count: Number.parseInt(this.getAttribute('data-collection-count') || '0', 10) || 0,
      entry_point: entryPoint || this.getAttribute('data-entry-point') || null,
      browse_source: this.getAttribute('data-browse-source') || 'global'
    });
  }
}

customElements.define('search-zero-state', SearchZeroState);

customElements.define('predictive-search', PredictiveSearch);

document.addEventListener('click', (event) => {
  if (event.target.closest && event.target.closest('search-visual-menu')) return;

  const cancel = event.target.closest && event.target.closest('[data-header-search-cancel]');
  if (cancel) {
    event.preventDefault();
    event.stopPropagation();
    const slot = cancel.closest('.header-search-bar-slot');
    const host = slot && slot.querySelector('predictive-search, search-form');
    if (host && typeof host.closeFromCancel === 'function') host.closeFromCancel();
    return;
  }

  const trigger = event.target.closest && event.target.closest('[data-header-search-trigger]');
  if (trigger) {
    event.preventDefault();
    event.stopPropagation();
    ensureSearchZeroState();
    const modal = document.querySelector('details-modal.header__search');
    if (modal && typeof modal.open === 'function') {
      if (typeof modal.isOpen === 'function' && modal.isOpen()) return;
      modal.open({ target: modal.querySelector('summary') || modal });
    }
  }
});

document.addEventListener(
  'pointerdown',
  (event) => {
    const target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    if (target.closest('search-visual-menu')) return;
    if (
      target.closest('[data-header-search-trigger]') ||
      target.closest('details-modal.header__search summary') ||
      target.closest('predictive-search input[type="search"]')
    ) {
      ensureSearchZeroState();
    }
  },
  true
);

class SearchVisualMenu extends HTMLElement {
  connectedCallback() {
    this.triggerBtn = this.querySelector('[data-search-visual-trigger]');
    this.panel = this.querySelector('[data-search-visual-panel]');
    if (!this.triggerBtn || !this.panel) return;

    this._onTriggerClick = this.onTriggerClick.bind(this);
    this._onDocClick = this.onDocClick.bind(this);
    this._onKeyDown = this.onKeyDown.bind(this);
    this._onOptionClick = this.onOptionClick.bind(this);

    this.triggerBtn.addEventListener('click', this._onTriggerClick);
    this.panel.addEventListener('click', this._onOptionClick);
  }

  disconnectedCallback() {
    if (this.triggerBtn) this.triggerBtn.removeEventListener('click', this._onTriggerClick);
    if (this.panel) this.panel.removeEventListener('click', this._onOptionClick);
    document.removeEventListener('click', this._onDocClick, true);
    document.removeEventListener('keydown', this._onKeyDown);
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  onTriggerClick(event) {
    event.preventDefault();
    event.stopPropagation();
    this.isOpen ? this.close() : this.openMenu();
  }

  onDocClick(event) {
    if (!this.contains(event.target)) this.close();
  }

  onKeyDown(event) {
    if (event.key === 'Escape') this.close(true);
  }

  onOptionClick(event) {
    const link = event.target.closest('[data-vs-option]');
    if (!link) return;
    fabPredictiveCapture('visual_search_option_clicked', {
      option: link.getAttribute('data-vs-option'),
      entry_point: this.getAttribute('data-entry-point') || 'header'
    });
  }

  openMenu() {
    document.querySelectorAll('search-visual-menu[open]').forEach((el) => {
      if (el !== this && typeof el.close === 'function') el.close();
    });
    this.setAttribute('open', '');
    this.triggerBtn.setAttribute('aria-expanded', 'true');
    this.panel.hidden = false;
    document.addEventListener('click', this._onDocClick, true);
    document.addEventListener('keydown', this._onKeyDown);
    const first = this.panel.querySelector('[data-vs-option]');
    if (first) first.focus();
    fabPredictiveCapture('visual_search_menu_opened', {
      entry_point: this.getAttribute('data-entry-point') || 'header'
    });
  }

  close(restoreFocus) {
    if (!this.isOpen) return;
    this.removeAttribute('open');
    this.triggerBtn.setAttribute('aria-expanded', 'false');
    this.panel.hidden = true;
    document.removeEventListener('click', this._onDocClick, true);
    document.removeEventListener('keydown', this._onKeyDown);
    if (restoreFocus) this.triggerBtn.focus();
  }
}

customElements.define('search-visual-menu', SearchVisualMenu);

function hydrateMegaNavImages(root) {
  if (!root) return;
  const nodes = root.querySelectorAll('[data-mega-image]:not([data-mega-image-loaded])');
  for (let i = 0; i < nodes.length; i++) {
    const el = nodes[i];
    const url = el.getAttribute('data-mega-image');
    const url2x = el.getAttribute('data-mega-image-2x');
    if (!url) continue;
    el.style.backgroundImage = 'url("' + (url2x || url) + '")';
    el.setAttribute('data-mega-image-loaded', '');
  }
}

window.hydrateMegaNavImages = hydrateMegaNavImages;

let megaNavImagePromise = null;

function loadMegaNavImages() {
  if (megaNavImagePromise) return megaNavImagePromise;

  const key = 'fab.meganav.images.v1';
  try {
    const cached = sessionStorage.getItem(key);
    if (cached) {
      megaNavImagePromise = Promise.resolve(JSON.parse(cached));
      return megaNavImagePromise;
    }
  } catch (_) {}

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  megaNavImagePromise = fetch(root + '?section_id=mega-nav-images', { credentials: 'same-origin' })
    .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
    .then((html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const payload = doc.querySelector('[data-mega-nav-images]');
      const map = payload ? JSON.parse(payload.textContent) : {};
      try {
        sessionStorage.setItem(key, JSON.stringify(map));
      } catch (_) {}
      return map;
    })
    .catch(() => ({}));

  return megaNavImagePromise;
}

function applyMegaNavImages(root, map) {
  if (!root) return;

  root.querySelectorAll('[data-mega-image-handle]:not([data-mega-image])').forEach((el) => {
    const urls = map[el.getAttribute('data-mega-image-handle')];
    if (!urls) return;
    const rectangle = el.classList.contains('mega-menu__link-image--rectangle');
    el.setAttribute('data-mega-image', rectangle ? urls[2] || urls[0] : urls[0]);
    el.setAttribute('data-mega-image-2x', rectangle ? urls[3] || urls[1] : urls[1]);
  });

  hydrateMegaNavImages(root);
}

let megaNavHydrationPromise = null;

function hydrateAllMegaNavImages() {
  if (megaNavHydrationPromise) return megaNavHydrationPromise;

  megaNavHydrationPromise = loadMegaNavImages().then((map) => {
    applyMegaNavImages(document.querySelector('.header__inline-menu'), map);
    return map;
  });

  return megaNavHydrationPromise;
}

class MegaMenu extends HTMLElement {
  constructor() {
    super();
    this.sidebarButtons = this.querySelectorAll('.mega-menu__sidebar-button');
    this.submenus = this.querySelectorAll('.mega-menu__submenu');
    this.defaultImages = this.querySelector('.mega-menu__default-images');
    this.megaMenuList = this.querySelector('.mega-menu__list');

    this.init();
  }

  init() {
    this.toggleDefaultImages(true);

    this.addEventListener('pointerenter', () => {
      this.hydrateDefault();
      hydrateAllMegaNavImages();
    });
    this.addEventListener('focusin', () => {
      this.hydrateDefault();
      hydrateAllMegaNavImages();
    });

    this.sidebarButtons.forEach((button) => {
      button.addEventListener('mouseenter', () => this.switchSubmenu(button));
    });

    this.addEventListener('mouseleave', () => {
      this.sidebarButtons.forEach((btn) =>
        btn.parentElement.classList.remove('mega-menu__sidebar-item--active')
      );
      this.submenus.forEach((submenu) => submenu.classList.remove('active'));
      this.toggleDefaultImages(true);
    });

    const details = this.querySelector('details');
    if (details) {
      details.addEventListener('toggle', () => {
        if (details.open) {
          this.hydrateDefault();
          hydrateAllMegaNavImages();
          const hasActiveSubmenu = Array.from(this.submenus).some((submenu) =>
            submenu.classList.contains('active')
          );
          if (!hasActiveSubmenu) {
            this.toggleDefaultImages(true);
          }
        }
      });
    }
  }

  hydrateDefault() {
    hydrateMegaNavImages(this.defaultImages);
  }

  hydrateSubmenu(submenu) {
    hydrateAllMegaNavImages().then(() => hydrateMegaNavImages(submenu));
  }

  switchSubmenu(button) {
    const submenuId = button.dataset.submenu;

    this.sidebarButtons.forEach((btn) =>
      btn.parentElement.classList.remove('mega-menu__sidebar-item--active')
    );
    this.submenus.forEach((submenu) => submenu.classList.remove('active'));

    button.parentElement.classList.add('mega-menu__sidebar-item--active');
    const submenu = this.querySelector('[data-submenu-content="' + submenuId + '"]');
    if (submenu) {
      submenu.classList.add('active');
      this.hydrateSubmenu(submenu);
    }

    this.toggleDefaultImages(false);
  }

  toggleDefaultImages(show) {
    if (this.defaultImages) {
      this.defaultImages.style.display = show ? 'flex' : 'none';
    }
    if (this.megaMenuList) {
      this.megaMenuList.style.display = show ? 'none' : 'block';
    }
  }
}

customElements.define('mega-menu', MegaMenu);

function bindDrawerMegaNavImages() {
  const details = document.getElementById('Details-menu-drawer-container');
  if (!details) return;

  const panel = () => details.querySelector('[data-mega-nav-panel]');
  const hydrate = () => hydrateMegaNavImages(panel());

  if (details.hasAttribute('data-mega-nav-images-bound')) {
    if (details.open) hydrate();
    return;
  }
  details.setAttribute('data-mega-nav-images-bound', '');

  const summary = details.querySelector('summary');
  if (summary) summary.addEventListener('pointerenter', hydrate);

  details.addEventListener('toggle', () => {
    if (details.open) hydrate();
  });
  if (details.open) hydrate();
}

window.bindDrawerMegaNavImages = bindDrawerMegaNavImages;
bindDrawerMegaNavImages();

(function () {
  'use strict';

  if (window.fabLoadSection) return;

  var TRACKING_RETRY_LIMIT = 20;
  var TRACKING_RETRY_MS = 100;

  function localePrefix(path) {
    if (!path || /^(?:https?:)?\/\//i.test(path)) return path;

    var locale = (window.Shopify && window.Shopify.locale) || 'en';
    locale = String(locale).trim();
    if (!locale || locale.toLowerCase() === 'en') return path;

    var normalizedPath = path.charAt(0) === '/' ? path : '/' + path;
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    if (root !== '/' && (normalizedPath === root.replace(/\/$/, '') || normalizedPath.indexOf(root) === 0)) {
      return normalizedPath;
    }

    var localeRoot = '/' + locale;
    if (normalizedPath === localeRoot || normalizedPath.indexOf(localeRoot + '/') === 0) {
      return normalizedPath;
    }
    return localeRoot + normalizedPath;
  }

  function appendQuery(url, query) {
    if (!query) return url;

    var params = [];
    if (typeof query === 'string') {
      params.push(query.replace(/^[?&]/, ''));
    } else if (typeof URLSearchParams !== 'undefined' && query instanceof URLSearchParams) {
      params.push(query.toString());
    } else {
      Object.keys(query).forEach(function (key) {
        var value = query[key];
        if (value === undefined || value === null || value === '') return;
        params.push(encodeURIComponent(key) + '=' + encodeURIComponent(String(value)));
      });
    }

    var serialized = params.filter(Boolean).join('&');
    if (!serialized) return url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + serialized;
  }

  function normalizeConfig(request, options) {
    var config = {};
    var key;

    if (typeof request === 'string') {
      config.url = request;
    } else if (request && typeof request === 'object') {
      for (key in request) {
        if (Object.prototype.hasOwnProperty.call(request, key)) config[key] = request[key];
      }
    }

    if (options && typeof options === 'object') {
      for (key in options) {
        if (Object.prototype.hasOwnProperty.call(options, key)) config[key] = options[key];
      }
    }

    return config;
  }

  function buildUrl(config) {
    var url = config.url;

    if (!url) {
      var resource = config.resource || 'products';
      var handle = config.handle || '';
      if (!handle || !config.section) return '';
      url = '/' + resource + '/' + encodeURIComponent(String(handle));
    }

    url = localePrefix(url);
    if (config.section) url = appendQuery(url, { section_id: config.section });
    return appendQuery(url, config.extraQuery);
  }

  function sectionId(config, url) {
    if (config.section) return String(config.section);
    var match = String(url).match(/[?&]section_id=([^&#]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  }

  function captureFailure(config, url, status, startedAt) {
    var details = {
      section_id: sectionId(config, url),
      status: status,
      url: url,
      ms: Math.max(0, Date.now() - startedAt)
    };

    console.warn('[fab-section-loader]', url, status);

    try {
      var helpers = window.__fabTrackingHelpers;
      if (helpers && typeof helpers.capture === 'function') {
        helpers.capture('section_load_failed', details);
      }
    } catch (error) {}
  }

  function notifyInjected(rootEl, attempt) {
    attempt = attempt || 0;
    try {
      var helpers = window.__fabTrackingHelpers;
      if (helpers && typeof helpers.notifyInjected === 'function') {
        helpers.notifyInjected(rootEl);
        return;
      }
    } catch (error) {}

    if (attempt + 1 < TRACKING_RETRY_LIMIT) {
      window.setTimeout(function () {
        notifyInjected(rootEl, attempt + 1);
      }, TRACKING_RETRY_MS);
    }
  }

  function replaceInlineScripts(rootEl) {
    Array.prototype.slice.call(rootEl.querySelectorAll('script')).forEach(function (script) {
      if (script.src || script.hasAttribute('src')) {
        script.remove();
        return;
      }

      var replacement = document.createElement('script');
      Array.prototype.slice.call(script.attributes).forEach(function (attribute) {
        replacement.setAttribute(attribute.name, attribute.value);
      });
      replacement.textContent = script.textContent || '';
      script.parentNode.replaceChild(replacement, script);
    });
  }

  function injectExpectedRoot(el, doc, selector) {
    var expectedRoot = doc.querySelector(selector);
    if (!expectedRoot) return false;

    el.replaceChildren();
    while (expectedRoot.firstChild) {
      el.appendChild(expectedRoot.firstChild);
    }

    replaceInlineScripts(el);
    notifyInjected(el);
    return true;
  }

  function shouldRetry(status, attempt) {
    return attempt === 0 && (status === 429 || status >= 500);
  }

  function retryDelay() {
    return 1500 + Math.random() * 1500;
  }

  function loadAttempt(el, config, url, attempt) {
    var startedAt = Date.now();

    return fetch(url, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
      .then(function (response) {
        if (!response.ok) {
          captureFailure(config, url, response.status, startedAt);
          if (shouldRetry(response.status, attempt)) {
            return new Promise(function (resolve) {
              window.setTimeout(resolve, retryDelay());
            }).then(function () {
              return loadAttempt(el, config, url, attempt + 1);
            });
          }
          return false;
        }

        return response.text().then(function (html) {
          var parser = new DOMParser();
          var doc = parser.parseFromString(html, 'text/html');
          var title = doc.querySelector('title');
          var isErrorPage = title && title.textContent.trim().toLowerCase() === 'something went wrong';
          var selector = config.expectSelector || '.shopify-section';

          if (isErrorPage || !doc.querySelector(selector)) {
            captureFailure(config, url, response.status, startedAt);
            return false;
          }

          return injectExpectedRoot(el, doc, selector);
        });
      })
      .catch(function () {
        captureFailure(config, url, 0, startedAt);
        return false;
      });
  }

  window.fabLoadSection = function (el, request, options) {
    if (!el) return Promise.resolve(false);

    var config = normalizeConfig(request, options);
    var url = buildUrl(config);
    if (!url) return Promise.resolve(false);

    return loadAttempt(el, config, url, 0);
  };

  try {
    document.dispatchEvent(new CustomEvent('fab:core-ready'));
  } catch (error) {}
})();

(function () {
  'use strict';

  function productsFromStorage() {
    try {
      var products = JSON.parse(localStorage.getItem('recently_viewed_products') || '[]');
      return Array.isArray(products) ? products : [];
    } catch (error) {
      return [];
    }
  }

  function imageUrl(source, width, quality) {
    if (!source) return '';
    try {
      var url = new URL(source, window.location.origin);
      url.searchParams.set('width', String(width));
      if (quality) url.searchParams.set('quality', String(quality));
      return url.toString();
    } catch (error) {
      return source;
    }
  }

  function safeProductUrl(source) {
    if (!source) return '#';
    try {
      var url = new URL(source, window.location.origin);
      return url.origin === window.location.origin ? url.pathname + url.search + url.hash : '#';
    } catch (error) {
      return '#';
    }
  }

  function appendPrice(container, product, prefix) {
    var price = String(product.price || '');
    var compareAt = String(product.compare_at_price || '');
    var priceNumber = Number(product.price);
    var compareNumber = Number(product.compare_at_price);
    var hasCompare =
      compareAt &&
      compareAt !== 'null' &&
      (!prefix || (Number.isFinite(priceNumber) && Number.isFinite(compareNumber) && compareNumber > priceNumber));

    var current = document.createElement('span');
    current.className = hasCompare ? 'product-card__price--sale' : 'product-card__price--regular';
    current.textContent = (prefix || '') + price;
    container.appendChild(current);

    if (!hasCompare) return;
    container.appendChild(document.createTextNode(' '));
    var previous = document.createElement('s');
    previous.className = 'product-card__price--regular';
    previous.textContent = compareAt;
    container.appendChild(previous);
  }

  function setTracking(element, productId, slot) {
    element.dataset.track = 'click';
    element.dataset.targetType = 'product';
    element.dataset.targetId = productId;
    element.dataset.slot = String(slot);
  }

  function createCarouselCard(product, slot) {
    var productId = String(product.id || '');
    var url = safeProductUrl(product.url);
    var title = String(product.title || '');

    var wrapper = document.createElement('div');
    wrapper.className = 'collection-carousel__product';
    var card = document.createElement('div');
    card.className = 'product-card';
    card.dataset.impressionItem = 'product';
    card.dataset.productId = productId;
    card.dataset.slot = String(slot);

    var media = document.createElement('div');
    media.className = 'product-card__media';
    var imageLink = document.createElement('a');
    imageLink.href = url;
    imageLink.className = 'product-card__link';
    setTracking(imageLink, productId, slot);
    var image = document.createElement('img');
    image.src = imageUrl(product.featured_image, 533);
    image.alt = title;
    image.className = 'product-card__image';
    image.width = 533;
    image.height = 533;
    image.loading = 'lazy';
    imageLink.appendChild(image);
    media.appendChild(imageLink);

    var content = document.createElement('div');
    content.className = 'product-card__content';
    var heading = document.createElement('h3');
    heading.className = 'product-card__title';
    var titleLink = document.createElement('a');
    titleLink.href = url;
    titleLink.className = 'full-unstyled-link';
    titleLink.textContent = title;
    setTracking(titleLink, productId, slot);
    heading.appendChild(titleLink);
    var price = document.createElement('div');
    price.className = 'product-card__price';
    appendPrice(price, product, '');

    content.append(heading, price);
    card.append(media, content);
    wrapper.appendChild(card);
    return wrapper;
  }

  function createGridCard(product, slot) {
    var productId = String(product.id || '');
    var url = safeProductUrl(product.url);
    var title = String(product.title || '');
    var source = product.featured_image || '';

    var item = document.createElement('div');
    item.className = 'grid__item';
    var wrapper = document.createElement('div');
    wrapper.className = 'card-wrapper product-card-wrapper';
    var card = document.createElement('div');
    card.className = 'product-card';
    card.dataset.impressionItem = 'product';
    card.dataset.productId = productId;
    card.dataset.slot = String(slot);

    var media = document.createElement('div');
    media.className = 'product-card__media';
    var imageLink = document.createElement('a');
    imageLink.href = url;
    imageLink.className = 'product-card__image-link product-card-link';
    setTracking(imageLink, productId, slot);
    var aspect = document.createElement('div');
    aspect.className = 'aspect-ratio-box relative overflow-hidden';
    aspect.style.aspectRatio = '225 / 292.5';
    var shimmer = document.createElement('div');
    shimmer.className = 'shimmer shimmer-blur absolute inset-0 z-0';
    shimmer.style.backgroundImage = 'url("' + imageUrl(source, 40, 10) + '")';
    shimmer.style.backgroundSize = 'cover';
    shimmer.style.backgroundPosition = 'center';
    var image = document.createElement('img');
    image.src = imageUrl(source, 300);
    image.srcset =
      imageUrl(source, 195) + ' 195w, ' + imageUrl(source, 300) + ' 300w, ' + imageUrl(source, 480) + ' 480w';
    image.sizes = '(max-width: 480px) 195px, (max-width: 768px) 300px, 480px';
    image.alt = title;
    image.className =
      'product-card__image--primary lazy-img absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-500 z-10';
    image.loading = slot < 8 ? 'eager' : 'lazy';
    image.decoding = 'async';
    image.addEventListener(
      'load',
      function () {
        image.classList.add('loaded');
        shimmer.remove();
      },
      { once: true }
    );
    aspect.append(shimmer, image);
    imageLink.appendChild(aspect);
    media.appendChild(imageLink);

    var content = document.createElement('div');
    content.className = 'product-card__content';
    var heading = document.createElement('h3');
    heading.className = 'product-card__title';
    var titleLink = document.createElement('a');
    titleLink.href = url;
    titleLink.className = 'product-card-link';
    titleLink.textContent = title;
    setTracking(titleLink, productId, slot);
    heading.appendChild(titleLink);
    var price = document.createElement('div');
    price.className = 'product-card__price';
    appendPrice(price, product, 'From ');

    content.append(heading, price);
    card.append(media, content);
    wrapper.appendChild(card);
    item.appendChild(wrapper);
    return item;
  }

  function render(root) {
    if (!root || root.dataset.fabRecentlyViewedReady === 'true') return;
    root.dataset.fabRecentlyViewedReady = 'true';

    var container = root.querySelector('[data-recently-viewed-items]');
    if (!container) return;
    var currentProductId = root.dataset.currentProductId || '';
    var limit = Number(root.dataset.productsToShow) || 12;
    var layout = root.dataset.recentlyViewedLayout || 'carousel';
    var products = productsFromStorage().filter(function (product) {
      return product && product.id && (!currentProductId || String(product.id) !== currentProductId);
    });

    container.replaceChildren();
    if (!products.length) {
      root.hidden = true;
      return;
    }

    products.slice(0, limit).forEach(function (product, slot) {
      container.appendChild(layout === 'grid' ? createGridCard(product, slot) : createCarouselCard(product, slot));
    });
    root.hidden = false;

    try {
      if (window.__fabTrackingHelpers && typeof window.__fabTrackingHelpers.notifyInjected === 'function') {
        window.__fabTrackingHelpers.notifyInjected(root);
      }
    } catch (error) {}
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-fab-recently-viewed]').forEach(render);
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-fab-recently-viewed] .collection-carousel__scroll-button');
    if (!button) return;
    var slider = button.closest('.collection-carousel__products-slider');
    var track = slider && slider.querySelector('[data-recently-viewed-items]');
    if (!track) return;
    track.scrollBy({
      left: button.classList.contains('scroll-left') ? -300 : 300,
      behavior: 'smooth'
    });
  });

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }
})();

(function () {
  'use strict';

  var configNode = document.querySelector('[data-fab-page-config]');
  var config = {};
  try {
    config = configNode ? JSON.parse(configNode.textContent || '{}') : {};
  } catch (error) {}

  window.FabAppConfig = config.app || {};
  window.FabCustomerSession = config.customer || null;
  window.FabLoginUrl = config.loginUrl || '';

  var PDP_SCENE_CACHE_PREFIX = 'fab_gallery_wall_pdp_scene_v1:';

  function normalizeProductId(value) {
    var id = typeof value === 'number' ? String(value) : String(value || '').trim();
    id = id.replace(/^gid:\/\/shopify\/Product\//, '');
    return /^[1-9]\d{7,19}$/.test(id) ? id : null;
  }

  function cacheCompanionWall(scene) {
    var seedId = normalizeProductId(scene && scene.seedId);
    if (!seedId) return;
    var country = String((window.Shopify && window.Shopify.country) || 'GB').toUpperCase();
    var language = String(
      (window.Shopify && window.Shopify.locale) || document.documentElement.lang || 'en'
    )
      .split('-')[0]
      .toUpperCase();
    try {
      sessionStorage.setItem(
        PDP_SCENE_CACHE_PREFIX + [country, language, seedId].join(':'),
        JSON.stringify({ savedAt: Date.now(), scene: scene })
      );
    } catch (error) {}
  }

  function onReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  }

  if (!window.__fabRecentlyViewedInit) {
    window.__fabRecentlyViewedInit = true;
    onReady(function () {
      var product = config.recentlyViewed;
      if (!product || !product.id) return;
      cacheCompanionWall(config.companionWall);
      var viewed = [];
      try {
        viewed = JSON.parse(localStorage.getItem('recently_viewed_products') || '[]');
        if (!Array.isArray(viewed)) viewed = [];
      } catch (error) {
        viewed = [];
      }
      viewed = viewed.filter(function (item) {
        return item && String(item.id) !== String(product.id);
      });
      viewed.unshift(product);
      try {
        localStorage.setItem('recently_viewed_products', JSON.stringify(viewed.slice(0, 100)));
      } catch (error) {}
    });
  }

  if (!window.__fabOpenInNewTabInit) {
    window.__fabOpenInNewTabInit = true;
    document.addEventListener('click', function (event) {
      var link = event.target.closest('.product-card-link');
      if (
        !link ||
        window.innerWidth < 768 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        event.button !== 0 ||
        event.defaultPrevented
      ) {
        return;
      }
      event.preventDefault();
      window.open(link.href, '_blank');
    });
  }

  if (!window.__fabLazyImgFixInit) {
    window.__fabLazyImgFixInit = true;
    onReady(function () {
      document.querySelectorAll('img.lazy-img[loading="lazy"]').forEach(function (image) {
        try {
          var src = image.getAttribute('src') || '';
          if (src && src.indexOf('width=40') === -1) return;
          var deferredSrc = image.getAttribute('data-src');
          if (deferredSrc) image.setAttribute('src', deferredSrc);
          image.classList.remove('lazy-img');
          var shimmer = image.previousElementSibling;
          if (shimmer && shimmer.classList.contains('shimmer')) shimmer.remove();
          image.classList.add('loaded');
        } catch (error) {}
      });
    });
  }

  function identifyFromEmail(email, extraProps) {
    if (
      !window.__fabPosthogIdentity ||
      typeof window.__fabPosthogIdentity.identifyTrustedEmail !== 'function'
    ) {
      return;
    }
    var normalized =
      window.__fabPosthogIdentity.normalizeEmail &&
      window.__fabPosthogIdentity.normalizeEmail(email);
    if (!normalized) return;
    window.__fabPosthogIdentity.identifyTrustedEmail(
      normalized,
      Object.assign({ identity_source: 'newsletter_signup' }, extraProps || {})
    );
    var fabAnonId =
      window.__fabPosthogIdentity.readFabAnonId &&
      window.__fabPosthogIdentity.readFabAnonId();
    try {
      if (window.posthog && typeof window.posthog.capture === 'function') {
        window.posthog.capture(
          'email_signup',
          Object.assign({ email: normalized, fab_anon_id: fabAnonId || null }, extraProps || {})
        );
      }
    } catch (error) {}
  }

  window.__fabIdentifyFromEmail = identifyFromEmail;

  document.addEventListener(
    'submit',
    function (event) {
      try {
        var form = event.target;
        if (!form || !form.querySelector) return;
        var input = form.querySelector('input[type="email"], input[name="email"], input[name="EMAIL"]');
        identifyFromEmail(input && input.value, { form_type: 'embedded' });
      } catch (error) {}
    },
    true
  );

  document.addEventListener(
    'click',
    function (event) {
      try {
        var form = event.target && event.target.closest && event.target.closest('form');
        if (!form || !form.querySelector) return;
        var input = form.querySelector('input[type="email"], input[name="email"], input[name="EMAIL"]');
        if (input && input.value) identifyFromEmail(input.value, { form_type: 'embedded_click' });
      } catch (error) {}
    },
    true
  );

  if (config.customer) {
    try {
      var identityKey = 'fab.identified.' + config.customer.customerId;
      if (!sessionStorage.getItem(identityKey)) {
        sessionStorage.setItem(identityKey, '1');
        if (
          window.__fabPosthogIdentity &&
          typeof window.__fabPosthogIdentity.identifyTrustedEmail === 'function'
        ) {
          window.__fabPosthogIdentity.identifyTrustedEmail(config.customer.email, {
            shopify_customer_id: config.customer.customerId,
            first_name: config.customer.firstName,
            identity_source: 'customer_login'
          });
        }
        window.klaviyo = window.klaviyo || [];
        window.klaviyo.push([
          'identify',
          {
            $email: config.customer.email,
            $first_name: config.customer.firstName,
            $last_name: config.customer.lastName,
            shopify_customer_id: config.customer.customerId
          }
        ]);
        if (window.fbq) window.fbq('init', '1248663493540230', { em: config.customer.email });
        if (window.gtag) window.gtag('set', 'user_data', { email: config.customer.email });
      }
    } catch (error) {}
  }

  window.addEventListener('fab-wishlist:toggle', function (event) {
    var detail = event.detail || {};
    var payload = {
      source: 'fab_wishlist',
      product_id: detail.productId,
      variant_id: detail.variantId || null,
      url: window.location.href,
      path: window.location.pathname,
      fab_anon_id: window.__fab_anon_id || null
    };
    try {
      window.__fabTrackingHelpers?.capture(detail.added ? 'wishlist_add' : 'wishlist_remove', payload);
    } catch (error) {}
    if (!detail.added) return;
    try {
      window.klaviyo = window.klaviyo || [];
      window.klaviyo.push(['track', 'Wishlist Added', payload]);
    } catch (error) {}
    try {
      if (window.fbq) {
        window.fbq('track', 'AddToWishlist', {
          content_ids: [detail.productId],
          content_type: 'product',
          currency: window.Shopify?.currency?.active || 'GBP'
        });
      }
    } catch (error) {}
  });

  window.addEventListener('fab-wishlist:migrated', function (event) {
    try {
      window.__fabTrackingHelpers?.capture('wishlist_migrated', {
        item_count: event.detail?.count || 0,
        source: 'wishlist_king',
        fab_anon_id: window.__fab_anon_id || null
      });
    } catch (error) {}
  });

  window._klOnsite = window._klOnsite || [];
  window._klOnsite.push([
    'openForm',
    function (form) {
      form.on('submit', function (event) {
        var email = event && event.email;
        if (email && window.fbq) {
          window.fbq('init', '1248663493540230', { em: email });
          window.fbq('track', 'Lead');
        }
      });
    }
  ]);

  console.log(
    '%c\n' +
      '  ███████  █████  ██████          \n' +
      '  ██      ██   ██ ██   ██  ▄█▄   \n' +
      '  █████   ███████ ██████  ▀███▀  \n' +
      '  ██      ██   ██ ██   ██  ▀█▀   \n' +
      '  ██      ██   ██ ██████    ▀    \n' +
      '\n' +
      '  fab-art.co\n',
    'color: #1a1817; font-family: monospace; line-height: 1.3;'
  );
})();
